import React, { useEffect, useState, useCallback } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Users, TrendingUp, Sparkles, Trash2, ShieldAlert, Activity, Mail, Rocket, Zap, Package,
} from 'lucide-react';

import OverviewTab    from './governance/OverviewTab';
import UsersTab       from './governance/UsersTab';
import ContentTab     from './governance/ContentTab';
import SentinelsTab   from './governance/SentinelsTab';
import AiTab          from './governance/AiTab';
import CleanupTab     from './governance/CleanupTab';
import PredictionsTab from './governance/PredictionsTab';
import AutomationTab  from './governance/AutomationTab';
import KnowledgeTab   from './governance/KnowledgeTab';

const TABS = [
  { key: 'overview',    label: 'Overview',       icon: TrendingUp },
  { key: 'predictions', label: 'Predictions',    icon: Rocket },
  { key: 'automation',  label: 'Automation',     icon: Zap },
  { key: 'users',       label: 'Users',          icon: Users },
  { key: 'content',     label: 'Content',        icon: Activity },
  { key: 'sentinels',   label: 'Sentinels',      icon: ShieldAlert },
  { key: 'ai',          label: 'AI Usage',       icon: Sparkles },
  { key: 'knowledge',   label: 'Knowledge',      icon: Package },
  { key: 'cleanup',     label: 'Cleanup Queue',  icon: Trash2 },
];

export default function GovernanceCenterPage() {
  const [tab, setTab] = useState('overview');
  const [summary, setSummary] = useState(null);
  const [adoption, setAdoption] = useState(null);
  const [users, setUsers] = useState(null);
  const [ai, setAi] = useState(null);
  const [queue, setQueue] = useState([]);
  const [counts, setCounts] = useState({});
  const [scanning, setScanning] = useState(false);
  const [digestSending, setDigestSending] = useState(false);

  const loadCleanup = useCallback(async () => {
    try {
      const q = await api.getCleanupQueue('pending');
      setQueue(q.items || []);
      setCounts(q.counts || {});
    } catch (e) {
      toast.error('Failed to load cleanup queue');
    }
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [s, a, u, ac] = await Promise.all([
          api.getGovernanceSummary(30),
          api.getGovernanceAdoption(),
          api.getGovernanceUserActivity(30),
          api.getGovernanceAiCost(30),
        ]);
        if (!alive) return;
        setSummary(s);
        setAdoption(a);
        setUsers(u.users || []);
        setAi(ac);
      } catch (e) {
        toast.error(e.message || 'Failed to load governance data');
      }
      loadCleanup();
    })();
    return () => { alive = false; };
  }, [loadCleanup]);

  const handleScan = async () => {
    setScanning(true);
    try {
      const res = await api.runCleanupScan(true);
      toast.success(`Scan complete — ${res.stale} stale, ${res.duplicates} dup, ${res.orphans} orphan, ${res.noisy_alerts} noisy, ${res.unused_ai_models} unused`);
      await loadCleanup();
    } catch (e) {
      toast.error(e.message || 'Scan failed');
    } finally {
      setScanning(false);
    }
  };

  const handleApprove = async (recId) => {
    try {
      await api.approveCleanup(recId);
      toast.success('Archived');
      await loadCleanup();
    } catch (e) {
      toast.error(e.message || 'Approve failed');
    }
  };
  const handleDismiss = async (recId) => {
    try {
      await api.dismissCleanup(recId);
      toast('Dismissed');
      await loadCleanup();
    } catch (e) {
      toast.error(e.message || 'Dismiss failed');
    }
  };

  const handleSendDigest = async () => {
    setDigestSending(true);
    try {
      const res = await api.sendDigestNow();
      if (res.ok) toast.success(`Digest sent to ${res.sent_to} admin${res.sent_to === 1 ? '' : 's'}`);
      else toast.warning(`Digest not sent: ${res.reason}`);
    } catch (e) {
      toast.error(e.message || 'Digest send failed');
    } finally {
      setDigestSending(false);
    }
  };

  const totalPending = Object.values(counts).reduce((a, b) => a + b, 0);

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="governance-page">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-50">Governance Overview</h1>
          <p className="text-sm text-slate-500 mt-1">Adoption, license utilization, AI cost, predictions & automated cleanup for your workspace.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleSendDigest} disabled={digestSending} data-testid="send-digest-btn">
            <Mail size={14} className="mr-1" />
            {digestSending ? 'Sending…' : 'Email digest now'}
          </Button>
        </div>
      </div>

      <div className="border-b border-slate-200 dark:border-slate-700 mb-6 -mx-6 px-6 overflow-x-auto">
        <div className="flex gap-4">
          {TABS.map(t => (
            <button
              key={t.key}
              data-testid={`gov-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 px-1 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                tab === t.key
                  ? 'border-slate-900 text-slate-900 dark:border-slate-100 dark:text-slate-100'
                  : 'border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
              }`}
            >
              <t.icon size={14} />{t.label}
              {t.key === 'cleanup' && totalPending > 0 && (
                <span className="ml-1 text-[10px] px-1.5 py-0.5 bg-red-500 text-white rounded-full font-semibold">
                  {totalPending}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {tab === 'overview'    && <OverviewTab summary={summary} adoption={adoption} />}
      {tab === 'predictions' && <PredictionsTab active={tab === 'predictions'} />}
      {tab === 'automation'  && <AutomationTab active={tab === 'automation'} />}
      {tab === 'users'       && <UsersTab users={users} />}
      {tab === 'content'     && <ContentTab adoption={adoption} />}
      {tab === 'sentinels'   && <SentinelsTab queue={queue} />}
      {tab === 'ai'          && <AiTab ai={ai} />}
      {tab === 'knowledge'   && <KnowledgeTab active={tab === 'knowledge'} />}
      {tab === 'cleanup'     && <CleanupTab queue={queue} counts={counts} onScan={handleScan} onApprove={handleApprove} onDismiss={handleDismiss} scanning={scanning} />}
    </div>
  );
}
